import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Building2, CircleCheck, CircleSlash, History, Loader2, Pencil, Plus, UserRound, Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/constants";

const EVENT_LABELS = {
  ALLOCATION_CREATED: "Allocation created",
  RECRUITER_CHANGE: "Recruiter change",
  STATUS_CHANGE: "Status change",
  FIELD_CHANGE: "Field change",
};

const EMPTY_FORM = {
  company_id: "",
  recruiter_ids: [""],
  allocation_type: "Single Recruiter",
  allocation_method: "Manual",
  active: true,
  effective_from: new Date().toISOString().slice(0, 10),
  effective_to: "",
  reason: "",
};

export default function AdminAllocations() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create mode
  const [form, setForm] = useState(EMPTY_FORM);

  const [historyOf, setHistoryOf] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(() => {
    setError(false);
    Promise.all([
      api.get("/allocations"),
      api.get("/companies", { params: { limit: 100 } }),
      api.get("/users/recruiters"),
    ])
      .then(([a, c, r]) => {
        setItems(a.data);
        setCompanies(c.data.items || c.data || []);
        setRecruiters((r.data || []).filter((x) => x.active !== false));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      company_id: a.company_id,
      recruiter_ids: a.recruiter_ids.length ? [...a.recruiter_ids] : [""],
      allocation_type: a.allocation_type,
      allocation_method: a.allocation_method,
      active: !!a.active,
      effective_from: a.effective_from || "",
      effective_to: a.effective_to || "",
      reason: "",
    });
    setFormOpen(true);
  };

  const setRecruiterAt = (idx, value) => {
    const ids = [...form.recruiter_ids];
    ids[idx] = value;
    setForm({ ...form, recruiter_ids: ids });
  };

  const setType = (t) => {
    let ids = form.recruiter_ids.filter(Boolean);
    if (t === "Single Recruiter") ids = ids.slice(0, 1);
    if (ids.length === 0) ids = [""];
    if (t === "Multiple Recruiters" && ids.length < 2) ids = [...ids, ""];
    setForm({ ...form, allocation_type: t, recruiter_ids: ids });
  };

  const submit = async () => {
    const ids = form.recruiter_ids.filter(Boolean);
    if (!form.company_id) return toast.error("Choose a company");
    if (ids.length === 0) return toast.error("Choose a recruiter");
    if (!form.effective_from) return toast.error("Effective From date is required");
    if (form.reason.trim().length < 3) return toast.error("A reason is required for every allocation change");
    setBusy(true);
    try {
      const payload = {
        recruiter_ids: ids,
        allocation_type: form.allocation_type,
        allocation_method: form.allocation_method,
        active: form.active,
        effective_from: form.effective_from,
        reason: form.reason.trim(),
      };
      if (editing) {
        if (form.effective_to) payload.effective_to = form.effective_to;
        else payload.clear_effective_to = true;
        const res = await api.put(`/allocations/${editing.id}`, payload);
        toast.success(`Allocation ${res.data.alloc_code} updated`);
      } else {
        payload.company_id = form.company_id;
        payload.effective_to = form.effective_to || null;
        const res = await api.post("/allocations", payload);
        toast.success(`Allocation ${res.data.alloc_code} created`);
      }
      setFormOpen(false);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "object" && d?.message ? d.message : getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openHistory = (a) => {
    setHistoryOf(a);
    setHistoryLoading(true);
    api.get(`/allocations/${a.id}/history`)
      .then((res) => setHistory(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setHistoryLoading(false));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Recruiter Allocation</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Assign recruiters to companies. New applications default to the allocated recruiter — existing applications are never changed.
          </p>
        </div>
        <Button onClick={openCreate} className="rounded-lg shrink-0" data-testid="allocation-create-button">
          <Plus className="h-4 w-4 mr-2" /> New Allocation
        </Button>
      </div>

      {error ? (
        <Card className="rounded-xl border-border shadow-none p-10 flex flex-col items-center gap-3" data-testid="allocations-error">
          <p className="text-sm text-muted-foreground">Could not load allocations.</p>
          <Button variant="outline" size="sm" className="rounded-lg bg-white" onClick={load}>Retry</Button>
        </Card>
      ) : items.length === 0 ? (
        <EmptyState icon={Users} title="No recruiter allocations yet"
          description="Create an allocation to route new applications of a company to its recruiter automatically." />
      ) : (
        <Card className="rounded-xl border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Assigned Recruiter</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Effective From</TableHead>
                  <TableHead>Updated On</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a.id} className="hover:bg-muted/60" data-testid="allocation-row">
                    <TableCell className="font-mono text-xs">{a.alloc_code}</TableCell>
                    <TableCell>
                      <p className="font-medium">{a.company?.name || "—"}</p>
                      <p className="text-xs text-muted-foreground font-mono">{a.company?.code}</p>
                    </TableCell>
                    <TableCell className="text-sm">
                      {(a.recruiters || []).map((r, i) => (
                        <span key={r.id}>
                          {i === 0 ? <span className="font-medium">{r.name}</span> : <span className="text-muted-foreground">, {r.name}</span>}
                        </span>
                      ))}
                      {a.allocation_type === "Multiple Recruiters" && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">first recruiter is the default</p>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{a.allocation_type}</TableCell>
                    <TableCell>
                      {a.active ? (
                        <Badge variant="outline" className="rounded-full bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="allocation-status-badge">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="rounded-full bg-slate-100 text-slate-600 border-slate-200" data-testid="allocation-status-badge">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(a.effective_from)}</TableCell>
                    <TableCell className="text-sm">{formatDate(a.updated_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white" onClick={() => openEdit(a)} data-testid={`allocation-edit-${a.id}`}>
                          <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => openHistory(a)} data-testid={`allocation-history-${a.id}`}>
                          <History className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={(v) => !v && setFormOpen(false)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[90vh] overflow-y-auto" data-testid="allocation-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editing ? `Update allocation — ${editing.alloc_code}` : "New recruiter allocation"}
            </DialogTitle>
            <DialogDescription>
              {editing ? "Every change requires a reason and is recorded permanently."
                : "One active allocation per company. History is kept forever."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> Company</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })} disabled={!!editing}>
                <SelectTrigger className="bg-white" data-testid="allocation-company-select"><SelectValue placeholder="Choose company" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {companies.length === 0 && <p className="text-[11px] text-amber-700">No companies in Company Master yet.</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Allocation type</Label>
                <Select value={form.allocation_type} onValueChange={setType}>
                  <SelectTrigger className="bg-white" data-testid="allocation-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Single Recruiter">Single Recruiter</SelectItem>
                    <SelectItem value="Multiple Recruiters">Multiple Recruiters</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Method</Label>
                <Select value={form.allocation_method} onValueChange={(v) => setForm({ ...form, allocation_method: v })}>
                  <SelectTrigger className="bg-white" data-testid="allocation-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Manual">Manual</SelectItem>
                    <SelectItem value="Round Robin">Round Robin (future)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5" /> Recruiter{form.allocation_type === "Multiple Recruiters" ? "s (first = default)" : ""}</Label>
              {form.recruiter_ids.map((rid, idx) => (
                <Select key={idx} value={rid} onValueChange={(v) => setRecruiterAt(idx, v)}>
                  <SelectTrigger className="bg-white" data-testid={`allocation-recruiter-select-${idx}`}>
                    <SelectValue placeholder={idx === 0 ? "Choose recruiter" : "Choose additional recruiter"} />
                  </SelectTrigger>
                  <SelectContent>
                    {recruiters.filter((r) => r.id === rid || !form.recruiter_ids.includes(r.id)).map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ))}
              {form.allocation_type === "Multiple Recruiters" && (
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" className="rounded-lg bg-white"
                    onClick={() => setForm({ ...form, recruiter_ids: [...form.recruiter_ids, ""] })}
                    data-testid="allocation-add-recruiter">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add recruiter
                  </Button>
                  {form.recruiter_ids.length > 2 && (
                    <Button type="button" size="sm" variant="ghost" className="rounded-lg"
                      onClick={() => setForm({ ...form, recruiter_ids: form.recruiter_ids.slice(0, -1) })}>
                      Remove last
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Effective From</Label>
                <Input type="date" className="bg-white" value={form.effective_from}
                  onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="allocation-effective-from" />
              </div>
              <div className="space-y-1.5">
                <Label>Effective To <span className="text-muted-foreground">(optional)</span></Label>
                <Input type="date" className="bg-white" value={form.effective_to}
                  onChange={(e) => setForm({ ...form, effective_to: e.target.value })} data-testid="allocation-effective-to" />
              </div>
            </div>

            <label className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 cursor-pointer">
              <span className="text-sm font-medium flex items-center gap-2">
                {form.active ? <CircleCheck className="h-4 w-4 text-emerald-600" /> : <CircleSlash className="h-4 w-4 text-slate-400" />}
                Active
              </span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="allocation-active-switch" />
            </label>

            <div className="space-y-1.5">
              <Label>Reason <span className="text-destructive">*</span></Label>
              <Textarea rows={2} className="bg-white" value={form.reason} placeholder="Why is this allocation being created/changed? (mandatory, audited)"
                onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="allocation-reason-input" />
            </div>

            <Button onClick={submit} disabled={busy} className="w-full rounded-lg" data-testid="allocation-save-button">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* History drawer */}
      <Sheet open={!!historyOf} onOpenChange={(v) => !v && setHistoryOf(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="allocation-history-drawer">
          <SheetHeader>
            <SheetTitle className="font-heading">Allocation history — {historyOf?.alloc_code}</SheetTitle>
            <SheetDescription>Immutable log of every allocation change. Never edited or deleted.</SheetDescription>
          </SheetHeader>
          <div className="space-y-2.5 mt-4">
            {historyLoading && <Skeleton className="h-24 w-full rounded-lg" />}
            {!historyLoading && history.length === 0 && <p className="text-sm text-muted-foreground">No history yet.</p>}
            {!historyLoading && history.map((e) => (
              <div key={e.id} className="rounded-lg border border-border p-3" data-testid="allocation-history-item">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="inline-flex rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                    {EVENT_LABELS[e.event_type] || e.event_type}
                  </span>
                  <span className="text-[11px] text-muted-foreground font-mono">{formatDateTime(e.at)}</span>
                </div>
                {(e.previous_recruiters || e.new_recruiters) && (
                  <p className="text-sm mt-1.5">
                    <span className="text-muted-foreground">recruiters: </span>
                    {e.previous_recruiters?.length ? e.previous_recruiters.join(", ") : "—"}
                    <span className="text-muted-foreground mx-1.5">→</span>
                    <span className="font-medium">{e.new_recruiters?.length ? e.new_recruiters.join(", ") : "—"}</span>
                  </p>
                )}
                {e.field && (
                  <p className="text-sm mt-1.5">
                    <span className="text-muted-foreground">{(e.field || "").replaceAll("_", " ")}: </span>
                    {String(e.old ?? "—")}<span className="text-muted-foreground mx-1.5">→</span>
                    <span className="font-medium">{String(e.new ?? "—")}</span>
                  </p>
                )}
                {e.reason && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">Reason: {e.reason}</p>}
                <p className="text-[11px] text-muted-foreground mt-1.5">by <span className="font-medium text-foreground">{e.actor_name}</span></p>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
